import { useEffect, useMemo, useRef, useState } from "react";
import { useParams, useSearchParams, Link } from "react-router-dom";
import { ShoppingBag, ArrowLeft, Loader2, ExternalLink, Instagram, Share2, Check, Trophy, Users } from "lucide-react";
import { toast } from "sonner";
import { API } from "../lib/api";
import { getPublicOrigin } from "../lib/publicOrigin";
import { WatchReelButton } from "../components/WatchReelButton";
import { StorefrontBuyModal } from "../components/commerce/StorefrontBuyModal";
import { ProductInfoSheet } from "../components/actionBlocks/commerce";
import EventAttendanceChip from "../components/EventAttendanceChip";
import { trackStorefront } from "../components/commerce/_shared";
import { useSaleCountdown } from "../lib/saleCountdown";
// v145.26 — Feed the signed-in user into the storefront card so the
// buy modal auto-fills email / name / phone. Without this, every
// StorefrontCard passed buyerUser=undefined and buyers had to
// re-type known details.
import { useAuth } from "../context/AuthContext";
import { externalLinkClickHandler } from "../lib/openExternal";
// v145.27 — Multi-product cart. StorefrontCard now offers "Add to
// cart" for products; services keep the single-Book-now flow.
import { StorefrontCartProvider, useStorefrontCart } from "../components/commerce/StorefrontCartContext";
import useCreatorLoyalty from "../hooks/useCreatorLoyalty";
import CoinEarnChip from "../components/CoinEarnChip";
import StorefrontCartBar from "../components/commerce/StorefrontCartBar";
import { sizedImage } from "../lib/imageUrl";
import { getProductSaleState, formatShortDate } from "../components/commerce/productSaleState";
import CompactStorefront, { STORE_PAGE_SIZE } from "../components/commerce/CompactStorefront";
import { ProductInitials } from "../components/commerce/ProductThumb";

// v142.3 — Re-export the pieces that moved into `components/commerce/`
// and `pages/SellPage.jsx` so existing import paths across the app
// (`import { StorefrontBuyModal, StorefrontCard } from "../pages/Storefront"`
//  and `import Storefront, { SellPage } from "./pages/Storefront"`)
// keep working without a churn PR across the codebase.
export { StorefrontBuyModal } from "../components/commerce/StorefrontBuyModal";
export { LoyaltyCoinPanel } from "../components/commerce/LoyaltyCoinPanel";
export { default as SellPage } from "./SellPage";

// Update `<head>` with OG/Twitter meta so social scrapers that render
// JS (Slack, Discord, iMessage, LinkedIn's newer bot, Twitter card
// validator) get a nice preview. Static SSR unfurls still need a
// dedicated route — tracked separately in the PRD.
function applyStorefrontHead({ creator, products, slug, total }) {
  if (typeof document === "undefined") return;
  const name = creator?.name || slug;
  const first = (products || [])[0];
  const title = `${name} — Storefront on Linkola`;
  // v149.69 — `total` (compact layout) counts every product; `products`
  // holds the 200 the page loaded.
  const n = Math.max(Number(total) || 0, products?.length || 0);
  const desc = n
    ? `${n} product${n === 1 ? "" : "s"} · Buy directly · Powered by Linkola`
    : `${name}'s Linkola storefront`;
  const img = first?.image_url || creator?.picture || "";
  const url = `${getPublicOrigin()}/store/${slug}`;
  document.title = title;
  const upsert = (attr, key, value) => {
    if (!value) return;
    let el = document.head.querySelector(`meta[${attr}="${key}"]`);
    if (!el) {
      el = document.createElement("meta");
      el.setAttribute(attr, key);
      document.head.appendChild(el);
    }
    el.setAttribute("content", value);
  };
  upsert("property", "og:type", "website");
  upsert("property", "og:title", title);
  upsert("property", "og:description", desc);
  upsert("property", "og:url", url);
  upsert("property", "og:image", img);
  upsert("name", "twitter:card", img ? "summary_large_image" : "summary");
  upsert("name", "twitter:title", title);
  upsert("name", "twitter:description", desc);
  upsert("name", "twitter:image", img);
  upsert("name", "description", desc);
}

/**
 * Storefront — public `/store/{slug}` page that lists every active
 * INR-priced product the creator has published. Buyers pick a product
 * and check out via Razorpay (same modal as the ProductBlock).
 *
 * Zero-auth — hits `/api/public/storefront/{slug}` which returns the
 * creator profile + product list. Non-INR products still render but
 * fall back to their external `buy_url` (or hide the Buy button when
 * neither is available).
 */
export default function Storefront() {
  const { slug } = useParams();
  // v149.69 — `?layout=compact|classic` previews a layout without saving it
  // (the seller's "Preview" links in Commerce → Storefront).
  const [searchParams] = useSearchParams();
  const previewLayout = searchParams.get("layout");
  const [data, setData] = useState(null);
  const [error, setError] = useState("");

  useEffect(() => {
    let alive = true;
    // v149.70 — `auto=1`: the server answers ONE page (60) when this shop
    // uses the compact layout, and the classic newest-200 otherwise — so a
    // big catalog never ships 200 products the page won't draw. A
    // `?layout=` preview is passed on so the answer matches what is shown.
    const first = new URLSearchParams({ auto: "1", limit: String(STORE_PAGE_SIZE) });
    if (["classic", "compact"].includes(previewLayout)) first.set("layout", previewLayout);
    fetch(`${API}/public/storefront/${encodeURIComponent(slug)}?${first}`)
      .then((r) => {
        if (!r.ok) throw new Error(r.status === 404 ? "Creator not found" : "Could not load storefront");
        return r.json();
      })
      .then((d) => { if (alive) setData(d); })
      .catch((e) => { if (alive) setError(e.message || "Something went wrong"); });
    return () => { alive = false; };
  }, [slug, previewLayout]);

  // Update OG/head + fire a single 'view' event whenever the storefront
  // finishes loading. We intentionally re-run per slug so navigating
  // between creators still counts each session as a distinct view.
  // The `trackedView` ref guards against React 18 StrictMode double-fire.
  const trackedView = useRef(null);
  useEffect(() => {
    if (!data) return;
    // The compact layout lists every product; classic lists what it loaded.
    const compactLayout = (["classic", "compact"].includes(previewLayout) ? previewLayout : data.settings?.layout) === "compact";
    applyStorefrontHead({ creator: data.creator, products: data.products, slug, total: compactLayout ? data.total : null });
    if (trackedView.current === slug) return;
    trackedView.current = slug;
    trackStorefront(slug, "view");
  }, [data, slug, previewLayout]);

  if (error) {
    return (
      <div className="min-h-screen bg-cream flex items-center justify-center p-6" data-testid="storefront-error">
        <div className="max-w-md text-center border-2 border-ink bg-white p-6 shadow-brutalSm">
          <p className="overline">Storefront</p>
          <h1 className="font-display font-black text-2xl mt-2">{error}</h1>
          <Link to="/" className="mt-4 inline-block border-2 border-ink bg-coral text-white px-3 py-1.5 font-bold text-sm shadow-brutalSm">
            Back home
          </Link>
        </div>
      </div>
    );
  }
  if (!data) {
    return (
      <div className="min-h-screen bg-cream flex items-center justify-center" data-testid="storefront-loading">
        <Loader2 className="w-6 h-6 animate-spin" />
      </div>
    );
  }
  const { creator, products } = data;
  // v145.27 — Read settings for cart requirements (phone / address).
  // Storefront settings live at the creator level; the block-level
  // config is applied when rendered inside a bio hub or action page.
  const requirePhone = !!data.settings?.cart_require_phone;
  const requireAddress = !!data.settings?.cart_require_address;
  const deliveryCharge = Number(data.settings?.cart_delivery_charge) || 0;
  // v149.69 — "compact": small tiles for shops with hundreds of products
  // (`CompactStorefront`). Anything else is the classic layout, unchanged.
  const layout = ["classic", "compact"].includes(previewLayout) ? previewLayout : data.settings?.layout;
  const compact = layout === "compact";
  const shell = compact ? "max-w-5xl mx-auto px-3 sm:px-6" : "max-w-4xl mx-auto px-4 sm:px-6";
  return (
    <StorefrontCartProvider slug={slug}>
    <StorefrontCartRestorer slug={slug} />
    <div className="min-h-screen bg-cream" data-testid="storefront-page" data-layout={compact ? "compact" : "classic"}>
      <header className="border-b-2 border-ink bg-white">
        <div className={`${shell} ${compact ? "py-3" : "py-6"} flex items-center ${compact ? "gap-3" : "gap-4"}`}>
          <Link to={`/u/${slug}`} className="border-2 border-ink bg-white p-1.5 shadow-brutalSm" data-testid="storefront-back-btn" aria-label="Back to Web Link">
            <ArrowLeft className="w-4 h-4" />
          </Link>
          {creator.picture ? (
            <img src={sizedImage(creator.picture, 160)} alt="" className={`${compact ? "w-10 h-10 flex-shrink-0" : "w-14 h-14"} rounded-full border-2 border-ink object-cover`} loading="eager" decoding="async" fetchPriority="high" />
          ) : (
            <div className={`${compact ? "w-10 h-10 flex-shrink-0" : "w-14 h-14"} rounded-full border-2 border-ink bg-coral text-white flex items-center justify-center font-black`}>
              {(creator.name || "?").charAt(0).toUpperCase()}
            </div>
          )}
          <div className="flex-1 min-w-0">
            <p className="overline">Storefront</p>
            <h1 className="font-display font-black text-xl sm:text-3xl tracking-tighter truncate" data-testid="storefront-creator-name">
              {creator.name}
            </h1>
            {creator.instagram_handle && (
              <a href={`https://instagram.com/${creator.instagram_handle.replace(/^@/, "")}`}
                 target="_blank" rel="noreferrer"
                 onClick={externalLinkClickHandler(`https://instagram.com/${creator.instagram_handle.replace(/^@/, "")}`)}
                 className="text-xs font-mono text-ink/60 hover:text-ink inline-flex items-center gap-1">
                <Instagram className="w-3 h-3" /> {creator.instagram_handle}
              </a>
            )}
          </div>
          <ShareStorefrontButton slug={slug} creatorName={creator.name} />
        </div>
      </header>

      <main className={`${shell} ${compact ? "py-3" : "py-8"}`}>
        {data.deals && data.deals.length > 0 && (
          <DealsStrip deals={data.deals} />
        )}
        {products.length > 0 && compact ? (
          <CompactStorefront
            key={slug}
            slug={slug}
            initial={data}
            settings={data.settings}
          />
        ) : products.length === 0 ? (
          <div className="border-2 border-dashed border-ink/40 bg-white p-10 text-center" data-testid="storefront-empty">
            <ShoppingBag className="w-10 h-10 mx-auto text-ink/40" />
            <p className="mt-3 font-bold text-lg">No products yet.</p>
            <p className="text-sm text-ink/60">The creator hasn&apos;t published any products.</p>
          </div>
        ) : (
          <>
            <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3" data-testid="storefront-grid">
              {products.map((p, i) => (
                <StorefrontCard
                  key={p.product_id}
                  product={p}
                  index={i}
                  slug={slug}
                  requireOtp={!!data.settings?.require_phone_otp}
                  otpChannel={data.settings?.otp_channel || "auto"}
                  codEnabled={!!data.settings?.cod_enabled}
                  onlineEnabled={data.settings?.online_enabled !== false}
                />
              ))}
            </div>
            <StorefrontCartBar
              slug={slug}
              buyerUser={null /* Storefront page reads auth via the modal itself */}
              requirePhone={requirePhone}
              requireAddress={requireAddress}
              deliveryCharge={deliveryCharge}
              codEnabled={!!data.settings?.cod_enabled}
              onlineEnabled={data.settings?.online_enabled !== false}
            />
          </>
        )}
      </main>

      <footer className={`${shell} pt-4 pb-16 text-center`}>
        <p className="text-xs text-ink/50">
          Storefront powered by <span className="font-bold">Linkola</span> · Secure checkout via Razorpay
        </p>
      </footer>
    </div>
    </StorefrontCartProvider>
  );
}

// ---------------------------------------------------------------------------
// DealsStrip — v109. Horizontal scroller of storefront-enabled coupon
// codes. Buyers see them right on the storefront so they don't have to
// know the code exists to save. Copy-to-clipboard on tap; the checkout
// modal auto-picks up the code via `?coupon=` URL param.
// ---------------------------------------------------------------------------
function DealsStrip({ deals }) {
  const [copied, setCopied] = useState("");
  const onCopy = async (code) => {
    try {
      await navigator.clipboard.writeText(code);
      setCopied(code);
      toast.success(`Copied ${code} — paste at checkout`);
      setTimeout(() => setCopied(""), 2000);
    } catch {
      toast.error("Couldn't copy");
    }
  };
  return (
    <section className="mb-6" data-testid="storefront-deals-strip">
      <p className="overline mb-2">Active deals</p>
      <div className="flex gap-3 overflow-x-auto pb-2 -mx-1 px-1 snap-x snap-mandatory">
        {deals.map((d, i) => {
          const wasCopied = copied === d.code;
          return (
            <button
              key={d.code}
              type="button"
              onClick={() => onCopy(d.code)}
              data-testid={`storefront-deal-${i}`}
              className="snap-start shrink-0 border-2 border-ink bg-mint px-3 py-2 shadow-brutalSm text-left min-w-[200px] hover:translate-y-[-1px] transition-transform"
            >
              <div className="flex items-center gap-2">
                <span className="font-display font-black text-2xl leading-none">
                  {d.discount_pct}%
                </span>
                <span className="text-[10px] font-bold uppercase tracking-wider">
                  off
                </span>
              </div>
              <div className="mt-1 flex items-center gap-1 text-[11px] font-bold">
                <code className="font-mono bg-white border-2 border-ink px-1.5 py-0.5 text-ink">
                  {d.code}
                </code>
                {wasCopied ? (
                  <Check className="w-3 h-3 text-ink" strokeWidth={3} />
                ) : (
                  <span className="text-ink/60">tap to copy</span>
                )}
              </div>
              {(d.description || d.min_purchase_inr) && (
                <p className="mt-1 text-[10px] text-ink/70 line-clamp-2">
                  {d.description || `Minimum cart ₹${Number(d.min_purchase_inr).toFixed(0)}`}
                </p>
              )}
              {d.expires_at && (
                <p className="mt-0.5 text-[9px] text-ink/50">
                  Ends {String(d.expires_at).slice(0, 10)}
                </p>
              )}
            </button>
          );
        })}
      </div>
    </section>
  );
}

// ---------------------------------------------------------------------------
// ShareStorefrontButton — uses the native Web Share API on mobile devices
// and falls back to clipboard copy on desktop browsers. Fires a
// `share_click` analytics event so creators can see how sharable their
// storefront is.
// ---------------------------------------------------------------------------
function ShareStorefrontButton({ slug, creatorName }) {
  const [copied, setCopied] = useState(false);
  const url = `${getPublicOrigin()}/store/${slug}`;
  const shareData = useMemo(() => ({
    title: `${creatorName || slug} — Storefront`,
    text: `Shop ${creatorName || slug}'s products directly on Linkola.`,
    url,
  }), [creatorName, slug, url]);

  const onClick = async () => {
    trackStorefront(slug, "share_click");
    if (navigator.share) {
      try { await navigator.share(shareData); return; } catch { /* user cancelled */ }
    }
    try {
      await navigator.clipboard.writeText(url);
      setCopied(true);
      setTimeout(() => setCopied(false), 1800);
      toast.success("Storefront link copied");
    } catch {
      toast.error("Could not copy link");
    }
  };

  return (
    <button
      type="button"
      onClick={onClick}
      data-testid="storefront-share-btn"
      className="border-2 border-ink bg-white px-3 py-2 font-bold text-xs shadow-brutalSm inline-flex items-center gap-1 flex-shrink-0"
      aria-label="Share storefront"
    >
      {copied ? <Check className="w-3.5 h-3.5" /> : <Share2 className="w-3.5 h-3.5" />}
      <span className="hidden sm:inline">{copied ? "Copied" : "Share"}</span>
    </button>
  );
}

// ---------------------------------------------------------------------------
// v147.73 — StorefrontCardGallery
// Compact multi-image carousel for storefront cards. Same pattern as the
// featured-product gallery but locked to the smaller card height and
// without the desktop-only arrow controls (tap+swipe is enough on a
// grid card).
// ---------------------------------------------------------------------------
function StorefrontCardGallery({ images, index }) {
  const [idx, setIdx] = useState(0);
  const ref = useRef(null);
  const onScroll = () => {
    const el = ref.current;
    if (!el) return;
    const w = el.clientWidth || 1;
    const cur = Math.round(el.scrollLeft / w);
    if (cur !== idx) setIdx(cur);
  };
  return (
    <div className="relative w-full h-44 border-b-2 border-ink bg-cream">
      <div
        ref={ref}
        onScroll={onScroll}
        className="w-full h-full flex overflow-x-auto snap-x snap-mandatory scroll-smooth scrollbar-none"
        data-testid={`storefront-card-gallery-${index}`}
      >
        {images.map((src, i) => (
          <div key={i} className="flex-shrink-0 w-full h-full snap-start flex items-center justify-center p-1">
            <img
              src={sizedImage(src, 960)}
              alt=""
              loading="lazy"
              decoding="async"
              className="max-w-full max-h-full w-auto h-auto object-contain"
              data-testid={i === 0 ? `storefront-card-img-${index}` : `storefront-card-img-${index}-${i}`}
            />
          </div>
        ))}
      </div>
      <div className="absolute bottom-1.5 left-1/2 -translate-x-1/2 flex gap-1">
        {images.map((_, i) => (
          <span
            key={i}
            className={`w-1.5 h-1.5 border border-ink ${i === idx ? "bg-ink" : "bg-white"}`}
          />
        ))}
      </div>
      <span className="absolute top-1.5 right-1.5 border border-ink bg-white text-[9px] font-black px-1 py-0.5">
        {idx + 1}/{images.length}
      </span>
    </div>
  );
}


export function StorefrontCard({ product, index, slug, requireOtp, otpChannel, codEnabled = false, onlineEnabled = true, requirePhone = false, requireAddress = false, deliveryCharge = 0 }) {
  const [showBuy, setShowBuy] = useState(false);
  // v147.99.10 — Product-info bottom sheet with the FULL description +
  // swipeable gallery. Opens on image / title / "Read more" tap so
  // grid-tiles get the same rich preview the FeaturedProductBlock has.
  const [showInfo, setShowInfo] = useState(false);
  // v145.26 — Grab the signed-in user so the buy modal can auto-fill
  // email / name / phone (see StorefrontBuyModal). Anonymous → null.
  const { user } = useAuth();
  // v145.26 — Image loading state drives the shimmer skeleton so the
  // card doesn't jank on first render. Starts `true` when there's an
  // image URL, flips to `false` on load or error.
  const [imgLoaded, setImgLoaded] = useState(!product.image_url);
  const [imgBroken, setImgBroken] = useState(false);
  // v149.69 — What can be bought, and when: one copy of the rules, shared
  // with the compact layout (`components/commerce/productSaleState.js`).
  const {
    sym, hasPrice, canBuyNow, isService, isTicket, ticketsLeft, isTicketSoldOut,
    winFrom, winTo, availStatus, availBlocked, availLabel,
  } = getProductSaleState(product);
  const _fmt = formatShortDate;
  const buyLabel = isTicket ? "Get tickets" : isService ? "Book now" : "Buy now";
  // v145.61 — When the creator has enabled a pass-on discount on their
  // affiliate pick, the public product doc carries `list_price_inr`
  // (shop price) + `customer_discount_pct` alongside `price` (which
  // is already the Linkola price). Show the strike-through when the
  // savings are real.
  const listPrice = Number(product.list_price_inr || 0);
  const discountPct = Number(product.customer_discount_pct || 0);
  const savings = Number(product.customer_savings_inr || 0);
  const hasCreatorDiscount = product.is_affiliate && discountPct > 0 && listPrice > Number(product.price || 0);
  // v145.62 — Social proof: "N customers bought this on Linkola".
  const socialProofCount = Number(product.social_proof_count || 0);
  const showSocialProof = !!product.is_affiliate && socialProofCount > 0;
  // v146.42 — Coin earn preview per card. Silent when disabled.
  const loyalty = useCreatorLoyalty(slug);
  const coinsPreview = loyalty.coinsForAmount(Number(product.price || 0));
  // v147.54 — Live countdown when the sale ends today (within 24h). Falls
  // back to the static chip above when > 24h remain or when there's no
  // end-date at all.
  const countdown = useSaleCountdown(availStatus === "active" ? winTo : null);
  return (
    <div className="border-2 border-ink bg-white shadow-brutalSm overflow-hidden flex flex-col" data-testid={`storefront-card-${index}`}>
      {(() => {
        // v147.73 — Multi-image gallery + no-crop. StorefrontCard grid
        // stays compact so we cap the height, but use `object-contain`
        // on a cream backdrop so tall event posters (Rox Event) and
        // wide product shots both show in full. Multi-image cards get
        // a snap-scroll carousel with dot indicators.
        const imgs = (product.image_urls && product.image_urls.length)
          ? product.image_urls
          : (product.image_url ? [product.image_url] : []);
        // v149.70 — No photo (or one that fails to load): the item's
        // initials, never an empty box or a broken-image icon.
        if (imgs.length === 0 || (imgs.length === 1 && imgBroken)) {
          return (
            <button
              type="button"
              onClick={() => setShowInfo(true)}
              data-testid={`storefront-card-image-btn-${index}`}
              className="block w-full h-44 border-b-2 border-ink overflow-hidden text-left"
              aria-label={`View details for ${product.title}`}
            >
              <ProductInitials name={product.title} className="w-full h-full" testid={`storefront-card-initials-${index}`} />
            </button>
          );
        }
        if (imgs.length === 1) {
          return (
            <button
              type="button"
              onClick={() => setShowInfo(true)}
              data-testid={`storefront-card-image-btn-${index}`}
              className="relative w-full h-44 border-b-2 border-ink overflow-hidden bg-cream cursor-zoom-in group block text-left"
              aria-label={`View details for ${product.title}`}
            >
              {!imgLoaded && (
                <div
                  className="absolute inset-0 animate-pulse bg-gradient-to-r from-cream via-white to-cream"
                  data-testid={`storefront-card-skeleton-${index}`}
                />
              )}
              <img
                src={sizedImage(imgs[0], 960)}
                alt=""
                loading="lazy"
                onLoad={() => setImgLoaded(true)}
                onError={() => { setImgLoaded(true); setImgBroken(true); }}
                className={`w-full h-full object-contain transition-opacity duration-300 group-hover:opacity-95 ${imgLoaded ? "opacity-100" : "opacity-0"}`}
                data-testid={`storefront-card-img-${index}`}
              />
            </button>
          );
        }
        return (
          <div onClick={() => setShowInfo(true)} className="cursor-zoom-in" data-testid={`storefront-card-gallery-wrap-${index}`}>
            <StorefrontCardGallery images={imgs} index={index} />
          </div>
        );
      })()}
      <div className="p-3 flex-1 flex flex-col gap-2">
        <div className="flex items-center gap-2 flex-wrap">
          <button type="button" onClick={() => setShowInfo(true)}
            className="font-bold text-base leading-tight flex-1 text-left hover:underline decoration-2 underline-offset-2"
            data-testid={`storefront-card-title-${index}`}
            aria-label={`View details for ${product.title}`}
          >{product.title}</button>
          {product.is_best_seller && (
            <span className="text-[10px] font-bold border border-ink px-1.5 py-0.5 bg-sun flex-shrink-0 inline-flex items-center gap-1" data-testid={`storefront-card-bestseller-badge-${index}`}>
              <Trophy className="w-2.5 h-2.5" strokeWidth={3} /> BEST SELLER
            </span>
          )}
          {product.is_affiliate && (
            <span className="text-[10px] font-bold border border-ink px-1.5 py-0.5 bg-mint flex-shrink-0" data-testid={`storefront-card-affiliate-badge-${index}`}>AFFILIATE</span>
          )}
          {isService && (
            <span className="text-[10px] font-bold border border-ink px-1.5 py-0.5 bg-lavender flex-shrink-0" data-testid={`storefront-card-service-badge-${index}`}>SERVICE</span>
          )}
          {isTicket && (
            <span className="text-[10px] font-bold border border-ink px-1.5 py-0.5 bg-coral text-white flex-shrink-0" data-testid={`storefront-card-ticket-badge-${index}`}>TICKET</span>
          )}
        </div>
        {/* v147.61 — Loyalty coin earn chip moved to the TOP of the card
            so buyers see the reward reward-first. Applies uniformly to
            products, services, and tickets — the loyalty rule is the
            same (₹ spent × earn-rate = coins earned, credited on paid
            orders regardless of `kind`). */}
        {coinsPreview > 0 && (canBuyNow || product.buy_url) && (
          <CoinEarnChip
            coins={coinsPreview}
            currentBalance={loyalty.balance}
            coinName={loyalty.coin_name}
            testId={`storefront-card-earn-${index}`}
            className="self-start"
          />
        )}
        {product.description && (
          <div>
            <p className="text-xs text-ink/60 line-clamp-3" data-testid={`storefront-card-desc-${index}`}>{product.description}</p>
            {String(product.description || "").length > 140 && (
              <button type="button" onClick={() => setShowInfo(true)}
                className="mt-1 text-[11px] font-black uppercase tracking-wider underline decoration-2 underline-offset-2 hover:text-coral"
                data-testid={`storefront-card-readmore-${index}`}
              >Read more →</button>
            )}
          </div>
        )}
        {showSocialProof && (
          <div
            className="inline-flex items-center gap-1 border-2 border-ink bg-mint px-1.5 py-0.5 text-[9px] font-black uppercase tracking-wider w-fit"            data-testid={`storefront-card-social-proof-${index}`}
          >
            <Users className="w-2.5 h-2.5" strokeWidth={2.5} />
            {socialProofCount.toLocaleString("en-IN")} bought
          </div>
        )}
        {product.social_link && (
          <WatchReelButton url={product.social_link} testid={`storefront-card-reel-${index}`} />
        )}
        {/* v145.26 — Stack price + CTA vertically so a large price
            (e.g. ₹1,000,000.00) doesn't collide with the button. The
            button spans the full card width for a bigger tap target on
            mobile too. Prices also use tabular-nums + hyphens-word to
            wrap gracefully if the currency + digits still overflow. */}
        <div className="mt-auto pt-2 flex flex-col gap-2 items-stretch">
          {hasPrice && (
            hasCreatorDiscount ? (
              // v145.61 — Creator pass-on discount surfaced on the tile.
              <div className="flex flex-col gap-0.5" data-testid={`storefront-card-price-${index}`}>
                <div className="flex items-baseline gap-2 flex-wrap">
                  <p className="font-display font-black text-lg leading-tight tracking-tight tabular-nums text-coral break-words"
                     data-testid={`storefront-card-linkola-price-${index}`}>
                    {sym}{Number(product.price).toLocaleString("en-IN")}
                  </p>
                  <p className="text-xs text-ink/50 line-through tabular-nums"
                     data-testid={`storefront-card-shop-price-${index}`}>
                    Shop {sym}{listPrice.toLocaleString("en-IN")}
                  </p>
                </div>
                <span className="inline-block self-start text-[9px] font-black uppercase tracking-wider border-2 border-ink bg-sun px-1.5 py-0.5"
                      data-testid={`storefront-card-savings-${index}`}>
                  Save {sym}{Math.round(savings)} · {discountPct}% off
                </span>
              </div>
            ) : (
              <p className="font-display font-black text-lg leading-tight tracking-tight tabular-nums break-words"
                 data-testid={`storefront-card-price-${index}`}>
                {isTicket && Array.isArray(product.ticket_tiers) && product.ticket_tiers.length > 1
                  ? <>from {sym}{Math.min(...product.ticket_tiers.map(t => Number(t.price || 0))).toFixed(0)}</>
                  : <>{sym}{Number(product.price).toFixed(2)}</>}
              </p>
            )
          )}
          {countdown ? (
            <p
              className={`text-[11px] font-black uppercase tracking-wider inline-flex items-center gap-1 px-2 py-0.5 border-2 border-ink self-start ${
                countdown.urgent ? "bg-coral text-white animate-pulse" : "bg-sun"
              }`}
              data-testid={`storefront-card-countdown-${index}`}
              aria-live="polite"
            >
              ⏰ {countdown.label}
            </p>
          ) : availLabel ? (
            <p
              className={`text-[11px] font-semibold inline-flex items-center gap-1 px-2 py-0.5 border-2 border-ink self-start ${
                availBlocked ? "bg-white text-coral" : "bg-mint"
              }`}
              data-testid={`storefront-card-availability-${index}`}
            >
              {availLabel}
            </p>
          ) : null}
          {isTicket && (
            <div className="flex flex-wrap items-center gap-1.5 text-[11px]">
              {product.event_date_from && (
                <span className="font-semibold" data-testid={`storefront-card-event-date-${index}`}>
                  📅 {_fmt(product.event_date_from)}
                  {product.event_date_to && product.event_date_to !== product.event_date_from
                    ? ` – ${_fmt(product.event_date_to)}` : ""}
                  {product.event_time ? ` · ${product.event_time}` : ""}
                </span>
              )}
              {product.venue_name && (
                <span className="text-ink/70" data-testid={`storefront-card-event-venue-${index}`}>
                  · 📍 {product.venue_name}
                </span>
              )}
              <span
                className={`ml-auto font-black uppercase px-1.5 py-0.5 border-2 border-ink ${
                  isTicketSoldOut ? "bg-coral text-white"
                  : ticketsLeft != null && ticketsLeft <= 10 ? "bg-sun"
                  : "bg-mint"
                }`}
                data-testid={`storefront-card-tickets-left-${index}`}
              >
                {isTicketSoldOut ? "Sold out" : ticketsLeft != null ? `${ticketsLeft} left` : "Tickets available"}
              </span>
            </div>
          )}
          {/* v147.93 — Public live attendance chip. Self-hides unless
              creator toggled `live_widget_enabled` for this ticket. */}
          {isTicket && (
            <div className="flex flex-wrap items-center gap-1.5 text-[11px] w-full mt-1">
              <EventAttendanceChip
                productId={product.product_id}
                testId={`storefront-card-attendance-chip-${index}`}
              />
            </div>
          )}
          {product.buy_url ? (
            <a href={product.buy_url} target="_blank" rel="noreferrer"
               onClick={availBlocked ? (e) => e.preventDefault() : externalLinkClickHandler(product.buy_url, () => { trackStorefront(slug, "product_click", product.product_id); trackStorefront(slug, "buy_click", product.product_id); })}
               className={`border-2 border-ink px-3 py-1.5 font-bold text-xs shadow-brutalSm inline-flex items-center justify-center gap-1 ${availBlocked ? "bg-white text-ink/50 cursor-not-allowed" : "bg-coral text-white"}`}
               aria-disabled={availBlocked ? "true" : undefined}
               data-testid={`storefront-buy-external-${index}`}>
              {availBlocked
                ? (availStatus === "ended" ? (isService ? "Bookings closed" : "Sold out")
                   : (isService ? `Opens ${_fmt(winFrom)}` : `Available ${_fmt(winFrom)}`))
                : (<>{isService ? "Book" : "Buy"} <ExternalLink className="w-3 h-3" /></>)}
            </a>
          ) : (isService || isTicket) && canBuyNow ? (
            // v145.27 — Services keep the direct Book-now flow (single
            // slot per checkout — carts are for products only).
            // v147.58 — Tickets also use the direct flow (quantity
            // stepper lives inside the buy modal).
            <button
              type="button"
              disabled={availBlocked || isTicketSoldOut}
              onClick={() => { trackStorefront(slug, "product_click", product.product_id); trackStorefront(slug, "buy_click", product.product_id); setShowBuy(true); }}
              className={`border-2 border-ink px-3 py-1.5 font-bold text-xs shadow-brutalSm inline-flex items-center justify-center gap-1 disabled:opacity-60 disabled:cursor-not-allowed ${(availBlocked || isTicketSoldOut) ? "bg-white text-ink/50" : "bg-coral text-white"}`}
              data-testid={`storefront-buy-now-${index}`}
            >
              <ShoppingBag className="w-3 h-3" /> {
                isTicketSoldOut ? "Sold out"
                : availBlocked
                  ? (availStatus === "ended" ? (isTicket ? "Sales closed" : "Bookings closed") : `Opens ${_fmt(winFrom)}`)
                  : buyLabel
              }
            </button>
          ) : canBuyNow && !availBlocked ? (
            // v145.27 — Products go through the cart. Users add-and-
            // browse, then check out once for the whole cart.
            <StorefrontProductCartActions product={product} index={index} slug={slug} />
          ) : canBuyNow && availBlocked ? (
            <button
              type="button"
              disabled
              className="border-2 border-ink bg-white text-ink/50 px-3 py-1.5 font-bold text-xs shadow-brutalSm inline-flex items-center justify-center gap-1 cursor-not-allowed"
              data-testid={`storefront-buy-blocked-${index}`}
            >
              {availStatus === "ended" ? "Sold out" : `Available ${_fmt(winFrom)}`}
            </button>
          ) : (
            // v145.60 — Products without a price shouldn't show ₹0 or a
            // Buy button. Surface a subtle "Contact seller" hint instead
            // (or nothing when a price exists but currency is not INR).
            !hasPrice && (
              <span className="text-[10px] text-ink/50 italic" data-testid={`storefront-card-nocta-${index}`}>
                Contact seller
              </span>
            )
          )}
        </div>
      </div>
      {showBuy && (
        <StorefrontBuyModal
          product={product}
          slug={slug}
          requireOtp={requireOtp}
          otpChannel={otpChannel}
          codEnabled={codEnabled}
          onlineEnabled={onlineEnabled}
          requirePhone={requirePhone}
          requireAddress={requireAddress}
          deliveryCharge={deliveryCharge}
          buyerUser={user}
          onClose={() => setShowBuy(false)}
          onSuccess={() => {
            toast.success(isService
              ? "Booking confirmed — the seller will get in touch."
              : "Payment received — the seller will get in touch.");
            trackStorefront(slug, "purchase", product.product_id);
            setShowBuy(false);
          }}
        />
      )}
      {/* v147.99.10 — Rich product info sheet: swipeable gallery + full
          description. Opens on image / title / "Read more" tap so grid
          tiles match the FeaturedProductBlock preview experience.
          v147.99.16 — In-sheet Buy/Book CTA so buyers can commit without
          closing the sheet. External buy_url routes off-site directly. */}
      <ProductInfoSheet
        open={showInfo}
        onClose={() => setShowInfo(false)}
        product={{ ...product, kind: isService ? "service" : (isTicket ? "ticket" : (product.kind || "product")) }}
        onCTA={(() => {
          if (isTicketSoldOut || availBlocked) return undefined; // still keeps disabled state visible
          if (product.buy_url) {
            return () => {
              setShowInfo(false);
              trackStorefront(slug, "product_click", product.product_id);
              trackStorefront(slug, "buy_click", product.product_id);
              window.open(product.buy_url, "_blank", "noopener,noreferrer");
            };
          }
          if (canBuyNow) {
            return () => {
              setShowInfo(false);
              trackStorefront(slug, "product_click", product.product_id);
              trackStorefront(slug, "buy_click", product.product_id);
              setShowBuy(true);
            };
          }
          return undefined;
        })()}
        ctaLabel={(() => {
          if (isTicketSoldOut) return "Sold out";
          if (availBlocked) return availStatus === "ended" ? "Sales closed" : `Opens ${_fmt(winFrom)}`;
          if (isService) return hasPrice ? `Book Now — ${sym}${Number(product.price).toFixed(0)}` : "Book Now";
          return hasPrice ? `Buy Now — ${sym}${Number(product.price).toFixed(0)}` : "Buy Now";
        })()}
        ctaTestId={`storefront-info-sheet-cta-${index}`}
        ctaDisabled={!!(isTicketSoldOut || availBlocked)}
      />
    </div>
  );
}

// v145.27 — Cart action row for products in the storefront grid. Uses
// the surrounding StorefrontCartProvider (mounted on the storefront
// products_grid block or the /store/{slug} page).
function StorefrontProductCartActions({ product, index, slug }) {
  const cart = useStorefrontCart();
  if (!cart) return null;
  const entry = cart.items[product.product_id];
  const inCart = !!entry;
  if (!inCart) {
    return (
      <button
        type="button"
        onClick={() => {
          try { trackStorefront(slug, "add_to_cart", product.product_id); } catch { /* noop */ }
          cart.add(product);
        }}
        className="border-2 border-ink bg-coral text-white px-3 py-1.5 font-bold text-xs shadow-brutalSm inline-flex items-center justify-center gap-1"
        data-testid={`storefront-add-to-cart-${index}`}
      >
        <ShoppingBag className="w-3 h-3" strokeWidth={3} /> Add to cart
      </button>
    );
  }
  return (
    <div className="flex items-center gap-2" data-testid={`storefront-cart-controls-${index}`}>
      <button type="button" onClick={() => cart.setUnits(product.product_id, entry.units - 1)}
              className="w-8 h-8 border-2 border-ink font-black bg-white" data-testid={`storefront-cart-dec-${index}`}>−</button>
      <span className="flex-1 text-center font-black tabular-nums">{entry.units}</span>
      <button type="button" onClick={() => cart.setUnits(product.product_id, entry.units + 1)}
              className="w-8 h-8 border-2 border-ink font-black bg-white" data-testid={`storefront-cart-inc-${index}`}>+</button>
    </div>
  );
}

// v145.28 — Cart restorer. Fires ONCE when the page mounts with a
// `?restore_cart=cart_id` query param (used by the abandoned-cart
// reminder email link). Fetches the saved item list and rehydrates the
// cart via `cart.restore()`, then strips the query param from the URL
// so a hard refresh doesn't re-run the restore.
function StorefrontCartRestorer({ slug }) {
  const cart = useStorefrontCart();
  const ran = useRef(false);
  useEffect(() => {
    if (ran.current || !cart) return;
    ran.current = true;
    const params = new URLSearchParams(window.location.search);
    const cartId = params.get("restore_cart");
    if (!cartId) return;
    (async () => {
      try {
        const r = await fetch(`${API}/public/storefront/cart/${encodeURIComponent(cartId)}`);
        if (!r.ok) return;
        const d = await r.json();
        if (Array.isArray(d.items) && d.items.length > 0) {
          cart.restore(d.items);
          toast.success(`Restored ${d.items.length} item${d.items.length === 1 ? "" : "s"} from your saved cart`);
        }
      } catch { /* silent */ }
      // Clean the URL so a page refresh doesn't re-run this.
      const url = new URL(window.location.href);
      url.searchParams.delete("restore_cart");
      window.history.replaceState({}, "", url.toString());
    })();
  }, []);
  return null;
}

